import { Validator } from 'fluentvalidation-ts';
import {
  beAnInteger,
  beGreaterThanOrEqualTo,
  beNumeric,
  beValidPercentage,
  haveDecimalPlaces,
} from '../../../../../validators/stringValidators';
import { FloorLevelCode } from '../../quoteOrDesignFormData';
import { UnitFormModel } from '../../units/unitFormModel';
import { defaultRoomTemperatureInDegreesCelsius, getRoomPositionalHeatingDefaults } from './roomHeatLossDefaults';

export type RoomRoofHeatLossDetailsFormModel = {
  maximumVaultHeightInMetres: string;
  roomAreaCoveredPercentage: string;
  additionalRoomVolumeInMetresCubed: string;
  skylightAreaInMetresSquared: string;
  temperatureAboveInDegreesCelsius: string;
};

export const getNewRoomRoofHeatLossDetailsFormModel = (
  ceilingHeightInMetres: number | null,
  roomFloorLevelCode: FloorLevelCode | null,
  externalAirTemperatureInDegreesCelsius: number,
  unit: UnitFormModel,
): RoomRoofHeatLossDetailsFormModel => ({
  maximumVaultHeightInMetres: ceilingHeightInMetres == null ? '' : ceilingHeightInMetres.toString(),
  roomAreaCoveredPercentage: '',
  additionalRoomVolumeInMetresCubed: '0',
  skylightAreaInMetresSquared: '0',
  temperatureAboveInDegreesCelsius: getRoomPositionalHeatingDefaults(
    roomFloorLevelCode,
    externalAirTemperatureInDegreesCelsius,
    unit,
  ).temperatureAboveInDegreesCelsius.toString(),
});

export const getDefaultRoofHeatLossDetailsFormModel = (
  defaultCeilingHeightInMetres: number,
  externalAirTemperature?: string,
): RoomRoofHeatLossDetailsFormModel => ({
  maximumVaultHeightInMetres: defaultCeilingHeightInMetres.toString(),
  roomAreaCoveredPercentage: '0',
  additionalRoomVolumeInMetresCubed: '0',
  skylightAreaInMetresSquared: '0',
  temperatureAboveInDegreesCelsius: externalAirTemperature ?? defaultRoomTemperatureInDegreesCelsius.toString(),
});

export class RoomRoofHeatLossDetailsFormModelValidator extends Validator<RoomRoofHeatLossDetailsFormModel> {
  constructor(
    totalRoomAreaCoveredPercentageAcrossAllRoofElements: number,
    ceilingHeight: string,
    heatLossDetailsAreRequired: boolean,
  ) {
    super();

    this.ruleFor('maximumVaultHeightInMetres')
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(2));

    this.ruleFor('maximumVaultHeightInMetres')
      .notEmpty()
      .must(maximumVaultHeightInMetres => Number(maximumVaultHeightInMetres) >= Number(ceilingHeight))
      .withMessage("Must be more than or equal to the room's ceiling height")
      .when(model => heatLossDetailsAreRequired && Number(model.roomAreaCoveredPercentage) !== 0);

    this.ruleFor('roomAreaCoveredPercentage')
      .must(beAnInteger)
      .must(beValidPercentage);

    this.ruleFor('roomAreaCoveredPercentage')
      .notEmpty()
      .must(() => totalRoomAreaCoveredPercentageAcrossAllRoofElements === 100)
      .when(() => heatLossDetailsAreRequired);

    this.ruleFor('additionalRoomVolumeInMetresCubed')
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1));

    this.ruleFor('additionalRoomVolumeInMetresCubed')
      .notEmpty()
      .when(model => heatLossDetailsAreRequired && Number(model.roomAreaCoveredPercentage) !== 0);

    this.ruleFor('skylightAreaInMetresSquared')
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1));

    this.ruleFor('skylightAreaInMetresSquared')
      .notEmpty()
      .when(model => heatLossDetailsAreRequired && Number(model.roomAreaCoveredPercentage) !== 0);

    this.ruleFor('temperatureAboveInDegreesCelsius')
      .must(beNumeric)
      .must(haveDecimalPlaces(1));

    this.ruleFor('temperatureAboveInDegreesCelsius')
      .notEmpty()
      .when(model => heatLossDetailsAreRequired && Number(model.roomAreaCoveredPercentage) !== 0);
  }
}
